import Box from '@mui/material/Box';
import { TitleWithSubtitle } from './atoms/TitleWithSubtitle';
import { BackButton } from './atoms/BackButton';
import { ConfigurationType } from '../types';
import { useDependencies } from '../hooks/useDependencies';
import { ActionOption, ButtonWithActionMenu } from './atoms/ButtonWithActionMenu';
import { Panel } from './atoms/Panel';
import { MiddlePanel } from './atoms/MiddlePanel';
import { ConfigurationItem } from '../../../parser/ParserTypes';
import { DependenciesSearchInput } from '../search/DependenciesSearchInput';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import Stack from '@mui/material/Stack';
import { DependenciesSearchEntryIcon } from '../search/DependenciesSearchEntryIcon';
import { useState, useCallback } from 'react';
import { Button } from '@sweep-io/sweep-design';
import { useDocumentationApiFacade } from '../../../../apis/facades/useDocumentationApiFacade';
import { telemetry } from '../../../../telemetry';

const downloadAsJson = (content: string, fileName: string) => {
  const jsonContent = JSON.stringify({ fileContent: content }, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface DependenciesHeaderProps {
  configurationItem: ConfigurationItem;
  type: ConfigurationType;
  crmOrgId: string;
  historyLength?: number;
  isReadOnly?: boolean;
  isOrgMigrationRoute?: boolean;
  showAskAiButton?: boolean;
  onAskAiClick?: () => void;
}

export const DependenciesHeader = ({
  configurationItem,
  type,
  crmOrgId,
  historyLength,
  isReadOnly = false,
  isOrgMigrationRoute = false,
  showAskAiButton = false,
  onAskAiClick,
}: DependenciesHeaderProps) => {
  const { searchInDocumentationCard } = useFeatureToggle();
  const { onGoBackInHistory } = useDependencies(crmOrgId);
  const { updatedBy, updatedAt, metadataId, name } = configurationItem ?? {};
  const { get_NaclFile } = useDocumentationApiFacade();
  const [isDownloading, setIsDownloading] = useState(false);

  const isObjectType = type === ConfigurationType.objects;
  const showBuildAgentButton = isOrgMigrationRoute && !isObjectType;

  const handleSendToBuildAgent = useCallback(async () => {
    if (!metadataId) return;

    setIsDownloading(true);
    try {
      const naclContent = await get_NaclFile({ crmOrgId, metadataId });
      const fileName = `${name || 'configuration'}.json`;
      downloadAsJson(naclContent, fileName);
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsDownloading(false);
    }
  }, [crmOrgId, metadataId, name, get_NaclFile]);

  return (
    <Panel sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={1}
        alignItems="center"
        width="100%"
      >
        {historyLength && historyLength > 1 && <BackButton onClick={onGoBackInHistory} />}

        <Box
          flex={1}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          width="100%"
        >
          <TitleWithSubtitle
            configurationItem={configurationItem}
            crmOrgId={crmOrgId}
            parentType={type}
          />

          <MiddlePanel>
            {showBuildAgentButton ? (
              <Stack sx={{ '& button': { whiteSpace: 'nowrap' } }}>
                <Button
                  variant="flat-grey"
                  size="small"
                  startIconName="Upload"
                  onClick={handleSendToBuildAgent}
                  loading={isDownloading}
                >
                  Send to Build Agent
                </Button>
              </Stack>
            ) : (
              <>
                {searchInDocumentationCard && <DependenciesSearchEntryIcon />}
                {showAskAiButton && (
                  <Stack sx={{ '& button': { whiteSpace: 'nowrap' } }}>
                    <Button
                      variant="primary"
                      size="tiny"
                      startIconName="MagicWand"
                      onClick={onAskAiClick}
                      dataTestId="ask-ai-button"
                    >
                      Ask AI
                    </Button>
                  </Stack>
                )}
                <ButtonWithActionMenu
                  lastUpdatedInfo={updatedBy && updatedAt ? { updatedBy, updatedAt } : undefined}
                  configurationItem={configurationItem}
                  crmOrgId={crmOrgId}
                  options={[
                    ActionOption.openInSalesforce,
                    ActionOption.viewSource,
                    ...(!isReadOnly && type !== ConfigurationType.objects
                      ? [ActionOption.comments]
                      : []),
                  ]}
                />
              </>
            )}
          </MiddlePanel>
        </Box>
      </Stack>
      {searchInDocumentationCard && <DependenciesSearchInput />}
    </Panel>
  );
};
